export const RESERVED_MEMBER_HANDLES = Object.freeze([
  "admin", "administrator", "help", "official", "portability", "security", "support"
] as const);

export type MemberPageTheme = "CLEAN_LIGHT" | "CLEAN_DARK" | "TRAVEL_BLUE";
export type SponsorConnectionStatus = "PENDING" | "CONNECTED" | "DECLINED" | "BLOCKED";
export type MemberPageReportReason = "ABUSE" | "FRAUD" | "IMPERSONATION" | "PROHIBITED_CONTENT" | "HARMFUL_LINK" | "OTHER";

export interface MemberPage {
  readonly accountId: string;
  readonly handle: string;
  readonly referralCode: string;
  readonly displayName: string;
  readonly bio: string;
  readonly profileImageUrl: string | null;
  readonly coverImageUrl: string | null;
  readonly contactLinks: readonly MemberPageLink[];
  readonly theme: MemberPageTheme;
  readonly publicVisible: boolean;
  readonly searchIndexOptIn: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly handlePermanent: true;
}

export interface MemberPageLink {
  readonly label: string;
  readonly url: string;
  readonly kind: "EMAIL" | "PHONE" | "WEBSITE" | "SOCIAL" | "OTHER";
}

export interface SponsorConnection {
  readonly connectionId: string;
  readonly sponsorAccountId: string;
  readonly memberAccountId: string;
  readonly status: SponsorConnectionStatus;
  readonly memberConsentedAt: string | null;
  readonly blockedAt: string | null;
  readonly sponsorNotifiedOfBlock: false;
}

export interface DirectMessage {
  readonly messageId: string;
  readonly connectionId: string;
  readonly senderAccountId: string;
  readonly recipientAccountId: string;
  readonly body: string;
  readonly sentAt: string;
  readonly format: "TEXT_ONLY";
  readonly attachmentsAllowed: false;
  readonly broadcastAllowed: false;
}

function requireText(value: string, field: string, maximumLength = 2_000) {
  const normalized = value.trim();
  if (normalized.length === 0) throw new Error(`${field} must not be blank`);
  if (normalized.length > maximumLength) throw new Error(`${field} exceeds the ${maximumLength}-character limit`);
  return normalized;
}

function canonicalIso(value: string, field: string) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString() !== value) throw new Error(`${field} must be canonical ISO`);
  return value;
}

function canonicalHandle(value: string) {
  const handle = value.trim().toLowerCase();
  if (!/^[a-z0-9](?:[a-z0-9_-]{1,28}[a-z0-9])$/.test(handle)) throw new Error("Member handle must be 3-30 lowercase letters, numbers, underscores, or hyphens");
  if ((RESERVED_MEMBER_HANDLES as readonly string[]).includes(handle)) throw new Error("Member handle is protected or reserved");
  return handle;
}

function assertHttpsUrl(value: string, field: string) {
  const url = new URL(value);
  if (url.protocol !== "https:") throw new Error(`${field} must use HTTPS`);
  if (url.username !== "" || url.password !== "") throw new Error(`${field} must not contain credentials`);
  return url.toString();
}

function validateLink(link: MemberPageLink) {
  requireText(link.label, "link label", 64);
  const rawUrl = link.url.trim();
  if (link.kind === "EMAIL") {
    if (!/^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawUrl)) throw new Error("Email links must use a valid mailto URL");
  } else if (link.kind === "PHONE") {
    if (!/^tel:\+[1-9][0-9]{6,14}$/.test(rawUrl)) throw new Error("Phone links must use an international tel URL");
  } else {
    assertHttpsUrl(rawUrl, "External member-page link");
  }
  return Object.freeze({ ...link, label: link.label.trim(), url: rawUrl });
}

export function createMemberPage(input: Omit<MemberPage, "handle" | "handlePermanent" | "contactLinks"> & { readonly handle: string; readonly contactLinks: readonly MemberPageLink[] }): MemberPage {
  requireText(input.accountId, "accountId", 128);
  requireText(input.referralCode, "referralCode", 128);
  requireText(input.displayName, "displayName", 80);
  if (input.bio.length > 500) throw new Error("Member-page bio exceeds the 500-character limit");
  canonicalIso(input.createdAt, "createdAt");
  canonicalIso(input.updatedAt, "updatedAt");
  if (Date.parse(input.updatedAt) < Date.parse(input.createdAt)) throw new Error("Member page cannot be updated before creation");
  if (input.contactLinks.length > 12) throw new Error("Member pages support at most 12 contact links");
  if (input.profileImageUrl !== null) assertHttpsUrl(input.profileImageUrl, "Profile image URL");
  if (input.coverImageUrl !== null) assertHttpsUrl(input.coverImageUrl, "Cover image URL");
  return Object.freeze({
    ...input,
    handle: canonicalHandle(input.handle),
    displayName: input.displayName.trim(),
    bio: input.bio.trim(),
    contactLinks: Object.freeze(input.contactLinks.map(validateLink)),
    handlePermanent: true as const
  });
}

export function updateMemberPage(page: MemberPage, input: { readonly handle?: string; readonly displayName?: string; readonly bio?: string; readonly profileImageUrl?: string | null; readonly coverImageUrl?: string | null; readonly contactLinks?: readonly MemberPageLink[]; readonly theme?: MemberPageTheme; readonly publicVisible?: boolean; readonly searchIndexOptIn?: boolean; readonly updatedAt: string }): MemberPage {
  if (input.handle !== undefined && canonicalHandle(input.handle) !== page.handle) throw new Error("Member handle is permanent once chosen");
  const publicVisible = input.publicVisible ?? page.publicVisible;
  const searchIndexOptIn = input.searchIndexOptIn ?? page.searchIndexOptIn;
  if (!publicVisible && searchIndexOptIn) throw new Error("A hidden member page cannot opt in to search indexing");
  return createMemberPage({
    ...page,
    displayName: input.displayName ?? page.displayName,
    bio: input.bio ?? page.bio,
    profileImageUrl: input.profileImageUrl === undefined ? page.profileImageUrl : input.profileImageUrl,
    coverImageUrl: input.coverImageUrl === undefined ? page.coverImageUrl : input.coverImageUrl,
    contactLinks: input.contactLinks ?? page.contactLinks,
    theme: input.theme ?? page.theme,
    publicVisible,
    searchIndexOptIn,
    updatedAt: input.updatedAt
  });
}

export function createReferralUrl(page: Pick<MemberPage, "handle" | "referralCode">, origin = "https://portabilityapp.net") {
  const url = new URL(`/member/${encodeURIComponent(page.handle)}/join`, assertHttpsUrl(origin, "Referral origin"));
  url.searchParams.set("ref", page.referralCode);
  return url.toString();
}

export function createPublicMemberPageView(page: MemberPage) {
  if (!page.publicVisible) throw new Error("Hidden member pages cannot be rendered publicly");
  return Object.freeze({
    handle: page.handle,
    displayName: page.displayName,
    bio: page.bio,
    profileImageUrl: page.profileImageUrl,
    coverImageUrl: page.coverImageUrl,
    contactLinks: page.contactLinks,
    theme: page.theme,
    referralUrl: createReferralUrl(page),
    robots: page.searchIndexOptIn ? "index,follow" as const : "noindex,nofollow" as const,
    joinCallToAction: "Join Portability" as const,
    footer: "Powered by Portability" as const,
    inAppDiscoverable: false as const,
    membershipTierExposed: false as const,
    earningsExposed: false as const,
    signupCountsExposed: false as const,
    privateTransactionsExposed: false as const
  });
}

export function recordPrivacySafeMemberPageAnalytics(input: { readonly eventId: string; readonly pageAccountId: string; readonly type: "PAGE_VIEW" | "REFERRAL_CLICK" | "JOIN_CLICK" | "ATTRIBUTED_SIGNUP" | "EXTERNAL_LINK_CLICK"; readonly campaignReference: string | null; readonly occurredAt: string }) {
  requireText(input.eventId, "eventId", 128);
  requireText(input.pageAccountId, "pageAccountId", 128);
  canonicalIso(input.occurredAt, "occurredAt");
  return Object.freeze({ ...input, visitorIdentityStored: false as const, rawIpStored: false as const });
}

export function createSponsorConnection(input: Omit<SponsorConnection, "status" | "memberConsentedAt" | "blockedAt" | "sponsorNotifiedOfBlock">): SponsorConnection {
  requireText(input.connectionId, "connectionId", 128);
  requireText(input.sponsorAccountId, "sponsorAccountId", 128);
  requireText(input.memberAccountId, "memberAccountId", 128);
  if (input.sponsorAccountId === input.memberAccountId) throw new Error("A sponsor connection cannot connect an account to itself");
  return Object.freeze({ ...input, status: "PENDING" as const, memberConsentedAt: null, blockedAt: null, sponsorNotifiedOfBlock: false as const });
}

export function acceptSponsorConnection(connection: SponsorConnection, memberAccountId: string, consentedAt: string): SponsorConnection {
  if (connection.memberAccountId !== memberAccountId) throw new Error("Only the referred member may consent to sponsor messaging");
  if (connection.status !== "PENDING") throw new Error("Only a pending sponsor connection may be accepted");
  canonicalIso(consentedAt, "consentedAt");
  return Object.freeze({ ...connection, status: "CONNECTED" as const, memberConsentedAt: consentedAt });
}

export function blockSponsorConnection(connection: SponsorConnection, memberAccountId: string, blockedAt: string): SponsorConnection {
  if (connection.memberAccountId !== memberAccountId) throw new Error("Only the referred member may block sponsor communication");
  canonicalIso(blockedAt, "blockedAt");
  return Object.freeze({ ...connection, status: "BLOCKED" as const, blockedAt, sponsorNotifiedOfBlock: false as const });
}

export function createSponsorWelcomeMessage(input: { readonly sponsorAccountId: string; readonly body: string; readonly updatedAt: string }) {
  requireText(input.sponsorAccountId, "sponsorAccountId", 128);
  const body = requireText(input.body, "welcome message", 1_000);
  canonicalIso(input.updatedAt, "updatedAt");
  return Object.freeze({ ...input, body, reusable: true as const, deliveryPerDirectSignup: "ONCE" as const, attachmentsAllowed: false as const });
}

export function createDirectMessage(connection: SponsorConnection, input: Omit<DirectMessage, "connectionId" | "format" | "attachmentsAllowed" | "broadcastAllowed">): DirectMessage {
  if (connection.status !== "CONNECTED") throw new Error("One-to-one messaging requires referred-member consent and an active connection");
  const participants = new Set([connection.sponsorAccountId, connection.memberAccountId]);
  if (!participants.has(input.senderAccountId) || !participants.has(input.recipientAccountId) || input.senderAccountId === input.recipientAccountId) {
    throw new Error("Direct messages must stay between the connected sponsor and referred member");
  }
  requireText(input.messageId, "messageId", 128);
  const body = requireText(input.body, "message body", 2_000);
  canonicalIso(input.sentAt, "sentAt");
  return Object.freeze({ ...input, body, connectionId: connection.connectionId, format: "TEXT_ONLY" as const, attachmentsAllowed: false as const, broadcastAllowed: false as const });
}

export function deleteConversationFromOwnView(input: { readonly connection: SponsorConnection; readonly accountId: string; readonly deletedAt: string }) {
  if (![input.connection.sponsorAccountId, input.connection.memberAccountId].includes(input.accountId)) throw new Error("Only a conversation participant may delete it from their own view");
  canonicalIso(input.deletedAt, "deletedAt");
  return Object.freeze({ connectionId: input.connection.connectionId, accountId: input.accountId, deletedAt: input.deletedAt, otherParticipantCopyDeleted: false as const });
}

export function createMemberPageReport(input: { readonly reportId: string; readonly reporterAccountId: string; readonly pageAccountId: string; readonly reason: MemberPageReportReason; readonly details: string; readonly reportedAt: string }) {
  requireText(input.reportId, "reportId", 128);
  requireText(input.reporterAccountId, "reporterAccountId", 128);
  requireText(input.pageAccountId, "pageAccountId", 128);
  if (input.reporterAccountId === input.pageAccountId) throw new Error("Members cannot report their own page");
  const details = requireText(input.details, "report details", 2_000);
  canonicalIso(input.reportedAt, "reportedAt");
  return Object.freeze({ ...input, details, status: "OPEN" as const, moderationRequired: true as const });
}
